import React, { useState, useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import Switch from "react-switch";
//pages & includes
import ManageSidebar from "../ManageSidebar";
//functions
import {
  _t,
  getCookie,
  tableLoading,
  customStyle,
} from "../../../../../functions/Functions";
//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SettingsContext } from "../../../../../contexts/Settings";
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CustomSpinLoader from "../../../../components/CustomSpinLoader";

const ZoneAdd = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const params = useParams();
  let {
    //common
    loading,
    setLoading,
  } = useContext(SettingsContext);

  let {
    //get branch
    branchForSearch,
    branchForListSearch,
    //pagination
    dataPaginating,
    areaForSearch,
    setAreaForSearch,
    zoneForSearch,
    setZoneForSearch,
    zoneList,
    setZoneList,
    getZones,
    areaList,
    setAreaList,
    getAreas,
  } = useContext(RestaurantContext);

  // States hook here
  let [thisZoneAreas, setThisZoneAreas] = useState([]);
  let [zoneAreasMap, setZoneAreasMap] = useState({});
  let [newZone, setNewZone] = useState({
    branch: null,
    name: "",
    areas: [],
    min_order: 0,
    delivery_charges: 0,
    delivery_estimation: 0,
    free_delivery_after: 0,
    free_delivery: 0,
    status: 1,
    edit: false,
    editSlug: null,
    uploading: false,
  });

  // Helper to extract area IDs from various formats (json string, array, objects)
  const extractAreaIds = (raw) => {
    if (!raw) return [];
    let parsed = raw;
    if (typeof raw === "string") {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        parsed = raw.split(",").map((s) => s.trim());
      }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (item === null || item === undefined) return null;
        if (typeof item === "object") {
          return String(
            item.area_id !== undefined
              ? item.area_id
              : item.id !== undefined
                ? item.id
                : "",
          );
        }
        return String(item);
      })
      .filter(Boolean);
  };

  // Combine all known zones
  const allZones = [
    ...(Array.isArray(zoneForSearch) ? zoneForSearch : []),
    ...(Array.isArray(zoneList?.data) ? zoneList.data : []),
  ];
  const uniqueZones = [];
  const seenZoneIds = new Set();
  for (const z of allZones) {
    if (!z) continue;
    const zId = String(
      z.zone_id !== undefined ? z.zone_id : z.id !== undefined ? z.id : "",
    );
    if (zId && !seenZoneIds.has(zId)) {
      seenZoneIds.add(zId);
      uniqueZones.push(z);
    }
  }

  // fetch all areas for dropdown
  const getAreasList = async () => {
    try {
      let list = [];
      let mapped = [];
      try {
        const res = await axios.get(BASE_URL + "/settings/get-area-list", {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        const d = res.data?.details;
        list = d?.areaList || d?.unMappedAreas || res.data?.data || [];
        mapped = d?.mappedAreas || [];
      } catch (e) {
        console.log("Error fetching get-area-list:", e);
      }

      if (!Array.isArray(list) || list.length === 0) {
        const res2 = await axios.get(BASE_URL + "/settings/areas", {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });
        const resData = res2.data;
        list = Array.isArray(resData)
          ? Array.isArray(resData[1])
            ? resData[1]
            : resData
          : resData?.details?.areaList || resData?.data || [];
      }

      if (Array.isArray(list) && list.length > 0) {
        setAreaForSearch(list);
      }
      if (Array.isArray(mapped) && mapped.length > 0) {
        setAreaList(mapped);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  useEffect(() => {
    getAreasList();
    if (getZones) {
      getZones();
    }
  }, []);

  // Fetch areas of zones if not present on zone objects
  useEffect(() => {
    const fetchZoneAreas = async () => {
      const newMap = {};
      const fetchList = [];

      for (const z of uniqueZones) {
        const zId = String(
          z.zone_id !== undefined ? z.zone_id : z.id !== undefined ? z.id : "",
        );
        if (!zId) continue;

        const directIds = extractAreaIds(z.areas);
        if (directIds.length > 0) {
          newMap[zId] = directIds;
        } else if (!zoneAreasMap[zId]) {
          fetchList.push(zId);
        }
      }

      if (fetchList.length > 0) {
        await Promise.all(
          fetchList.map(async (zId) => {
            try {
              const res = await axios.get(`${BASE_URL}/settings/zones/${zId}`, {
                headers: { Authorization: `Bearer ${getCookie()}` },
              });
              const areas =
                res.data?.details?.areas ||
                res.data?.details?.zone?.areas ||
                [];
              const ids = extractAreaIds(areas);
              if (ids.length > 0) {
                newMap[zId] = ids;
              }
            } catch (err) {
              // ignore
            }
          }),
        );
      }

      if (Object.keys(newMap).length > 0) {
        setZoneAreasMap((prev) => ({ ...prev, ...newMap }));
      }
    };

    if (uniqueZones.length > 0) {
      fetchZoneAreas();
    }
  }, [zoneForSearch, zoneList]);

  //set states on edit
  const handleSetEdit = (id) => {
    const getZoneEdit = async () => {
      try {
        const areasUrl = BASE_URL + `/settings/zones/${id}`;

        const response = await axios.get(areasUrl, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });

        const zoneEdit = response.data?.details?.zone;

        const branchEdit =
          branchForListSearch && zoneEdit
            ? branchForListSearch[zoneEdit.branch_id]
            : null;

        const formattedZoneAreas = response.data?.details?.areas || [];

        setThisZoneAreas(formattedZoneAreas);

        setNewZone((prev) => ({
          ...prev,
          branch: branchEdit,
          areas: formattedZoneAreas,
          name: zoneEdit?.name || "",
          min_order: zoneEdit?.minimum || 0,
          delivery_charges: zoneEdit?.fee || 0,
          delivery_estimation: zoneEdit?.delivery_estimation || 0,
          free_delivery_after: zoneEdit?.min_for_free_delivery || 0,
          free_delivery: zoneEdit?.free_delivery || 0,
          status: zoneEdit?.status !== undefined ? zoneEdit.status : 1,
          edit: true,
          editSlug: id,
        }));
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };

    getZoneEdit();
  };

  useEffect(() => {
    if (params.id) {
      handleSetEdit(params.id);
    } else {
      setThisZoneAreas([]);
      setNewZone((prev) => ({
        ...prev,
        edit: false,
        editSlug: null,
      }));
    }
  }, [params.id, branchForSearch]);

  const handleSetAreas = (area) => {
    setNewZone({
      ...newZone,
      areas: area || [],
    });
  };

  // branch select
  const handleSetNewBranch = (branch) => {
    setNewZone({
      ...newZone,
      branch: branch,
    });
  };
  // satus change
  const handleChecked = () => {
    setNewZone({
      ...newZone,
      status: newZone.status == 0 ? 1 : 0,
    });
  };

  const handleChange = (e) => {
    setNewZone({
      ...newZone,
      [e.target.name]: e.target.value,
    });
  };

  //add zone
  const handleSaveNewZone = (e) => {
    e.preventDefault();
    if (
      newZone.branch === null ||
      newZone.branch === "" ||
      !newZone.areas ||
      !Array.isArray(newZone.areas) ||
      newZone.areas.length === 0
    ) {
      toast.error(`${_t(t("Please select all required fields"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    } else {
      setNewZone({
        ...newZone,
        uploading: true,
      });

      let area = newZone.areas.map((a) =>
        a.area_id !== undefined ? a.area_id : a.id,
      );
      const zoneUrl = BASE_URL + "/settings/zones";
      let formData = {
        branch_id: newZone.branch.id,
        name: newZone.name,
        areas: JSON.stringify(area),
        minimum: newZone.min_order ? parseFloat(newZone.min_order) : 0,
        fee: newZone.delivery_charges
          ? parseFloat(newZone.delivery_charges)
          : 0,
        free_delivery: newZone.free_delivery,
        delivery_estimation: newZone.delivery_estimation
          ? parseFloat(newZone.delivery_estimation)
          : 0,
        min_for_free_delivery: newZone.free_delivery_after
          ? parseFloat(newZone.free_delivery_after)
          : 0,
        status: newZone.status,
      };
      return axios
        .post(zoneUrl, formData, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          const rawAreas =
            res.data?.details?.unMappedAreas || res.data?.details?.areaList;

          if (Array.isArray(rawAreas)) {
            setAreaForSearch(rawAreas);
          } else {
            getAreasList();
          }
          if (Array.isArray(res.data?.details?.mappedAreas)) {
            setAreaList(res.data.details.mappedAreas);
          }
          if (getAreas) {
            getAreas();
          }
          if (getZones) {
            getZones();
          }
          setZoneList(res.data.details?.pagination);
          setZoneForSearch(res.data.details?.zoneList);
          setThisZoneAreas([]);
          setNewZone({
            branch: null,
            name: "",
            areas: [],
            min_order: 0,
            delivery_charges: 0,
            delivery_estimation: 0,
            free_delivery_after: 0,
            free_delivery: 0,
            status: 1,
            edit: false,
            editSlug: null,
            uploading: false,
          });
          setLoading(false);
          toast.success(`${_t(t("Zone has been added"))}`, {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          });
          history.push("/dashboard/manage/restaurant/zones");
        })
        .catch((error) => {
          console.log("check error==>", error);
          setLoading(false);
          setNewZone({
            ...newZone,
            uploading: false,
          });
          toast.error(`${_t(t("Please try again"))}`, {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            pauseOnHover: true,
            className: "text-center toast-notification",
          });
        });
    }
  };

  // update zone
  const handleUpdateZone = (e) => {
    e.preventDefault();
    if (
      newZone.branch === null ||
      newZone.branch === "" ||
      !newZone.areas ||
      !Array.isArray(newZone.areas) ||
      newZone.areas.length === 0
    ) {
      toast.error(`${_t(t("Please select all required fields"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    } else {
      setNewZone({
        ...newZone,
        uploading: true,
      });
      let area = newZone.areas.map((a) =>
        a.area_id !== undefined ? a.area_id : a.id,
      );
      const zoneUrl = BASE_URL + `/settings/zones/${newZone.editSlug}`;
      let formData = {
        branch_id: newZone.branch.id,
        name: newZone.name,
        areas: JSON.stringify(area),
        minimum: newZone.min_order ? parseFloat(newZone.min_order) : 0,
        fee: newZone.delivery_charges
          ? parseFloat(newZone.delivery_charges)
          : 0,
        free_delivery: newZone.free_delivery,
        delivery_estimation: newZone.delivery_estimation
          ? parseFloat(newZone.delivery_estimation)
          : 0,
        min_for_free_delivery: newZone.free_delivery_after
          ? parseFloat(newZone.free_delivery_after)
          : 0,
        status: newZone.status,
      };

      return axios
        .put(zoneUrl, formData, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          const rawAreas =
            res.data?.details?.unMappedAreas || res.data?.details?.areaList;
          if (Array.isArray(rawAreas)) {
            setAreaForSearch(rawAreas);
          } else {
            getAreasList();
          }
          if (Array.isArray(res.data?.details?.mappedAreas)) {
            setAreaList(res.data.details.mappedAreas);
          }
          if (getAreas) {
            getAreas();
          }
          if (getZones) {
            getZones();
          }
          setZoneList(res.data.details?.pagination);
          setZoneForSearch(res.data.details?.zoneList);
          setThisZoneAreas([]);
          setNewZone({
            branch: null,
            name: "",
            areas: [],
            min_order: 0,
            delivery_charges: 0,
            delivery_estimation: 0,
            free_delivery_after: 0,
            free_delivery: 0,
            status: 1,
            edit: false,
            editSlug: null,
            uploading: false,
          });
          history.push("/dashboard/manage/restaurant/zones");
          setLoading(false);
          setNewZone({
            ...newZone,
            uploading: false,
          });
          toast.success(`${_t(t("Zone has been updated"))}`, {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          });
        })
        .catch((error) => {
          setNewZone({
            ...newZone,
            uploading: false,
          });
          setLoading(false);
          toast.error(`${_t(t("Please try again"))}`, {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            pauseOnHover: true,
            className: "text-center toast-notification",
          });
        });
    }
  };

  // Condition: areas selected in other zones should not be shown
// Areas already assigned to other zones
const otherZoneAreaIds = new Set();

uniqueZones.forEach((zone) => {
  const zoneId = String(zone.zone_id ?? zone.id ?? "");

  // Don't exclude areas of the zone currently being edited
  if (
    newZone.edit &&
    zoneId === String(newZone.editSlug)
  ) {
    return;
  }

  const areas = zoneAreasMap[zoneId] || extractAreaIds(zone.areas);

  areas.forEach((id) => {
    otherZoneAreaIds.add(String(id));
  });
});

// Only show areas that are not already assigned
const areaOptions = (Array.isArray(areaForSearch) ? areaForSearch : []).filter(
  (area) => {
    const areaId = String(area.area_id ?? area.id ?? "");
    return areaId && !otherZoneAreaIds.has(areaId);
  }
);

  return (
    <div>
      <Helmet>
        <title>{_t(t("Zone"))}</title>
      </Helmet>
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* Loading effect */}
                    {/* {newZone.uploading === true || loading === true ? (
                  tableLoading()
                  ) : ( */}
                    <div key="fragment3">
                      {/* next page data spin loading */}
                      <CustomSpinLoader
                        loading={loading || dataPaginating || newZone.uploading}
                      />
                      {/* spin loading ends */}

                      <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb">
                            <li className="fk-breadcrumb__list">
                              <span className="t-link fk-breadcrumb__link text-capitalize">
                                {!newZone.edit
                                  ? _t(t("Add New Zone"))
                                  : _t(t("Update Zone"))}
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="col-md-6 col-lg-7">
                          <div className="row gx-3 align-items-center"></div>
                        </div>
                      </div>
                      {/* form start here */}
                      <div key="fragment-branch-1">
                        <form
                          onSubmit={
                            !newZone.edit ? handleSaveNewZone : handleUpdateZone
                          }
                          className="row card p-2 mx-3 mb-5 new-text-style"
                          autoComplete="off"
                        >
                          <div className="row">
                            <div className="form-group col-6 mt-3">
                              <label htmlFor="name" className="form-label">
                                {_t(t("Select Branch"))}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Select
                                options={branchForSearch && branchForSearch}
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                                classNamePrefix="Branch"
                                className="sm-text"
                                maxMenuHeight="200px"
                                placeholder="Branch"
                                styles={customStyle}
                                value={newZone.branch || ""}
                                required
                                onChange={handleSetNewBranch}
                                isClearable
                              />
                            </div>
                            <div className="form-group mt-3 col-6">
                              <label htmlFor="zone" className="form-label">
                                {_t(t("Zone Name"))}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control "
                                id="name"
                                name="name"
                                placeholder="e.g. karachi East"
                                value={newZone.name || ""}
                                required
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="form-group col-6 mt-3">
                              <label htmlFor="name" className="form-label">
                                {_t(t("Select Areas"))}{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Select
                                options={areaOptions}
                                getOptionLabel={(option) =>
                                  option.name ||
                                  option.area_name ||
                                  option.area ||
                                  ""
                                }
                                getOptionValue={(option) =>
                                  String(
                                    option.area_id !== undefined
                                      ? option.area_id
                                      : option.id !== undefined
                                        ? option.id
                                        : "",
                                  )
                                }
                                isMulti={true}
                                classNamePrefix="Area"
                                className="sm-text"
                                maxMenuHeight="200px"
                                placeholder="Area"
                                styles={customStyle}
                                value={
                                  Array.isArray(newZone.areas)
                                    ? newZone.areas
                                    : []
                                }
                                onChange={handleSetAreas}
                              />
                            </div>
                            <div className="form-group mt-3 col-6">
                              <label htmlFor="min_order" className="form-label">
                                {_t(t("Minimum Order"))}{" "}
                              </label>
                              <input
                                type="number"
                                className="form-control "
                                id="min_order"
                                name="min_order"
                                onKeyDown={(evt) =>
                                  [
                                    "e",
                                    "E",
                                    "+",
                                    "-",
                                    "ArrowUp",
                                    "ArrowDown",
                                  ].includes(evt.key) && evt.preventDefault()
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                placeholder="e.g. 0"
                                value={newZone.min_order || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="form-group col-4 mt-3">
                              <label
                                htmlFor="delivery_charges"
                                className="form-label"
                              >
                                {_t(t("Delivery Charges"))}{" "}
                              </label>
                              <input
                                type="number"
                                className="form-control "
                                id="delivery_charges"
                                name="delivery_charges"
                                onKeyDown={(evt) =>
                                  [
                                    "e",
                                    "E",
                                    "+",
                                    "-",
                                    "ArrowUp",
                                    "ArrowDown",
                                  ].includes(evt.key) && evt.preventDefault()
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                placeholder="e.g. 0"
                                value={newZone.delivery_charges || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="form-group col-2 mt-3 d-flex align-items-center gap-2">
                              <label
                                htmlFor="free_delivery"
                                className="form-label mb-0"
                              >
                                {_t(t("Free Delivery"))}
                              </label>
                              <input
                                type="checkbox"
                                id="free_delivery"
                                name="free_delivery"
                                className="pointer-cursor"
                                style={{
                                  margin: 0,
                                  position: "static",
                                }}
                                onChange={() => {
                                  setNewZone({
                                    ...newZone,
                                    free_delivery:
                                      parseInt(newZone.free_delivery) === 0
                                        ? 1
                                        : 0,
                                  });
                                }}
                                checked={
                                  parseInt(newZone.free_delivery) === 1
                                }
                              />
                            </div>

                            <div className="form-group mt-3 col-6">
                              <label
                                htmlFor="delivery_estimation"
                                className="form-label"
                              >
                                {_t(t("Delivery Time"))}{" "}
                              </label>
                              <input
                                type="number"
                                className="form-control "
                                id="delivery_estimation"
                                name="delivery_estimation"
                                onKeyDown={(evt) =>
                                  [
                                    "e",
                                    "E",
                                    "+",
                                    "-",
                                    "ArrowUp",
                                    "ArrowDown",
                                  ].includes(evt.key) && evt.preventDefault()
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                placeholder="e.g. 0"
                                value={newZone.delivery_estimation || ""}
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="form-group col-6 mt-3">
                              <label
                                htmlFor="free_delivery_after"
                                className="form-label"
                              >
                                {_t(t("Free Delivery After"))}{" "}
                              </label>
                              <input
                                type="number"
                                className="form-control "
                                id="free_delivery_after"
                                name="free_delivery_after"
                                onKeyDown={(evt) =>
                                  [
                                    "e",
                                    "E",
                                    "+",
                                    "-",
                                    "ArrowUp",
                                    "ArrowDown",
                                  ].includes(evt.key) && evt.preventDefault()
                                }
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                placeholder="e.g. 0"
                                value={newZone.free_delivery_after || ""}
                                onChange={handleChange}
                              />
                            </div>

                            <div className="form-group mt-3 col-6">
                              <label
                                htmlFor="status"
                                className="form-label mb-2"
                              >
                                {_t(t("Status"))}{" "}
                              </label>
                              <div className="mt-1 ml-1">
                                <Switch
                                  onChange={handleChecked}
                                  checked={newZone.status === 1}
                                  className="react-switch ml-1"
                                  name="status"
                                  height={20}
                                  width={42}
                                  offColor="#ee5253"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="row mt-4">
                            <div className="d-flex justify-content-end">
                              <div className="col-3 d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn rounded hov-btn mr-3"
                                  onClick={() =>
                                    history.push(
                                      "/dashboard/manage/restaurant/zones",
                                    )
                                  }
                                >
                                  <i
                                    className="fa fa-arrow-left"
                                    aria-hidden="true"
                                  ></i>{" "}
                                  {_t(t("Back"))}
                                </button>
                                <button
                                  type="submit"
                                  className="btn btn-primary rounded"
                                >
                                  <i
                                    className="fa fa-floppy-o"
                                    aria-hidden="true"
                                  ></i>{" "}
                                  {!newZone.edit
                                    ? _t(t("Save"))
                                    : _t(t("Update"))}
                                </button>
                              </div>
                              {/* <div className="col-3">
                                 <button
                                   type="button"
                                   className="btn btn-primary w-100 x text-uppercase t-width-max rounded"
                                   onClick={()=>(history.push("/dashboard/manage/restaurant/branches"))}
                                 >
                                   {_t(t("BACK"))}
                                 </button>
                               </div> */}
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ZoneAdd;
